import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTemplatesList, getWhatsappAccount, deleteTemplate, getTemplate } from "../../../api/login.service";

// Lightweight date formatter (no extra deps)
function formatDate(d: string | number | Date | undefined) {
  if (!d) return '--';
  const date = typeof d === 'number' ? new Date(d * 1000) : new Date(d);
  if (isNaN(date.getTime())) return '--';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Map Graph API status -> badge style + label
function statusBadge(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED') return { label: 'Active • Quality', className: 'bg-green-100 text-green-700 border-green-200' };
  if (s === 'REJECTED') return { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' };
  if (s === 'PAUSED') return { label: 'Paused', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
  return { label: s || 'Pending', className: 'bg-gray-100 text-gray-700 border-gray-200' };
}

// Known language labels (fallback to raw code)
const LANGUAGE_LABELS: Record<string, string> = {
  af: 'Afrikaans',
  sq: 'Albanian',
  ar: 'Arabic',
  ar_EG: 'Arabic (EGY)',
  ar_AE: 'Arabic (UAE)',
  ar_LB: 'Arabic (LBN)',
  ar_MA: 'Arabic (MAR)',
  ar_QA: 'Arabic (QAT)',
  az: 'Azerbaijani',
  be_BY: 'Belarusian',
  bn: 'Bengali',
  bn_IN: 'Bengali (IND)',
  bg: 'Bulgarian',
  ca: 'Catalan',
  zh_CN: 'Chinese (CHN)',
  zh_HK: 'Chinese (HKG)',
  zh_TW: 'Chinese (TAI)',
  hr: 'Croatian',
  cs: 'Czech',
  da: 'Danish',
  prs_AF: 'Dari',
  nl: 'Dutch',
  nl_BE: 'Dutch (BEL)',
  en: 'English',
  en_GB: 'English (UK)',
  en_US: 'English (US)',
  en_AE: 'English (UAE)',
  en_AU: 'English (AUS)',
  en_CA: 'English (CAN)',
  en_GH: 'English (GHA)',
  en_IE: 'English (IRL)',
  en_IN: 'English (IND)',
  en_JM: 'English (JAM)',
  en_MY: 'English (MYS)',
  en_NZ: 'English (NZL)',
  en_QA: 'English (QAT)',
  en_SG: 'English (SGP)',
  en_UG: 'English (UGA)',
  en_ZA: 'English (ZAF)',
  et: 'Estonian',
  fil: 'Filipino',
  fi: 'Finnish',
  fr: 'French',
  fr_BE: 'French (BEL)',
  fr_CA: 'French (CAN)',
  fr_CH: 'French (CHE)',
  fr_CI: 'French (CIV)',
  fr_MA: 'French (MAR)',
  ka: 'Georgian',
  de: 'German',
  de_AT: 'German (AUT)',
  de_CH: 'German (CHE)',
  el: 'Greek',
  gu: 'Gujarati',
  ha: 'Hausa',
  he: 'Hebrew',
  hi: 'Hindi',
  hu: 'Hungarian',
  id: 'Indonesian',
  ga: 'Irish',
  it: 'Italian',
  ja: 'Japanese',
  kn: 'Kannada',
  kk: 'Kazakh',
  rw_RW: 'Kinyarwanda',
  ko: 'Korean',
  ky_KG: 'Kyrgyz (Kyrgyzstan)',
  lo: 'Lao',
  lv: 'Latvian',
  lt: 'Lithuanian',
  mk: 'Macedonian',
  ms: 'Malay',
  ml: 'Malayalam',
  mr: 'Marathi',
  nb: 'Norwegian',
  ps_AF: 'Pashto',
  fa: 'Persian',
  pl: 'Polish',
  pt_BR: 'Portuguese (BR)',
  pt_PT: 'Portuguese (POR)',
  pa: 'Punjabi',
  ro: 'Romanian',
  ru: 'Russian',
  sr: 'Serbian',
  si_LK: 'Sinhala',
  sk: 'Slovak',
  sl: 'Slovenian',
  es: 'Spanish',
  es_AR: 'Spanish (ARG)',
  es_CL: 'Spanish (CHL)',
  es_CO: 'Spanish (COL)',
  es_CR: 'Spanish (CRI)',
  es_DO: 'Spanish (DOM)',
  es_EC: 'Spanish (ECU)',
  es_HN: 'Spanish (HND)',
  es_MX: 'Spanish (MEX)',
  es_PA: 'Spanish (PAN)',
  es_PE: 'Spanish (PER)',
  es_ES: 'Spanish (SPA)',
  es_UY: 'Spanish (URY)',
  sw: 'Swahili',
  sv: 'Swedish',
  ta: 'Tamil',
  te: 'Telugu',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  ur: 'Urdu',
  uz: 'Uzbek',
  vi: 'Vietnamese',
  zu: 'Zulu',
};

// Types for our API response
interface TemplateRow {
  id: string;
  name: string;
  category?: string;
  language?: string;
  status?: string;
  last_updated_time?: string | number;
  components?: any[];
  form_url?: string;
}

interface ListResponse {
  data?: TemplateRow[];
  paging?: {
    cursors?: { before?: string; after?: string };
    next?: string;
    previous?: string;
  };
}

export default function WhatsappTemplatesList() {
  const navigate = useNavigate();
  
  // Get wabaId from session storage
  const wabaId = sessionStorage.getItem('wabaId');
  
  console.log('WhatsappTemplatesList: wabaId from session storage:', wabaId);
  
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [after, setAfter] = useState<string | undefined>(undefined);
  const [before, setBefore] = useState<string | undefined>(undefined);
  const [cursor, setCursor] = useState<{ after?: string; before?: string }>({});
  
  // Get phoneNumberId from WhatsApp accounts
  const [phoneNumberId, setPhoneNumberId] = useState<string>('');
  
  // Delete state
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  // Template preview state
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [templateData, setTemplateData] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Remove update template state since we're using a separate edit page
  // const [editingTemplate, setEditingTemplate] = useState<TemplateRow | null>(null);
  // const [updateLoading, setUpdateLoading] = useState(false);
  // const [updateError, setUpdateError] = useState<string | null>(null);
  // const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);

  // Get phoneNumberId from WhatsApp accounts when component mounts
  useEffect(() => {
    const getPhoneNumberId = async () => {
      try {
        // Get the email from localStorage or auth context
        const email = localStorage.getItem('email');
        if (email) {
          const accounts = await getWhatsappAccount(email);
          const account = accounts.find((acc: any) => acc.wabaId === wabaId);
          if (account) {
            setPhoneNumberId(account.phoneNumberId);
          }
        }
      } catch (error) {
        console.error('Failed to get phoneNumberId:', error);
      }
    };
    
    if (wabaId) {
      getPhoneNumberId();
    }
  }, [wabaId]);

  const fields = useMemo(
    () => ['id', 'name', 'category', 'language', 'status', 'last_updated_time'].join(','),
    []
  );

  async function fetchPage({ after, before }: { after?: string; before?: string } = {}) {
    if (!wabaId) {
      setError('No wabaId provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data: ListResponse = await getTemplatesList(wabaId, 25, after, before);
      setRows(data.data || []);
      setCursor({
        after: data.paging?.cursors?.after,
        before: data.paging?.cursors?.before,
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPage({ after, before });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [after, before, wabaId]);

  const handleDelete = async (templateId: string) => {
    setDeletingTemplateId(templateId);
    setError(null);
    try {
      await deleteTemplate(templateId);
      // Refresh the list after deletion
      await fetchPage({ after, before });
    } catch (e: any) {
      setError(e?.message || 'Failed to delete template');
    } finally {
      setDeletingTemplateId(null);
      setShowDeleteConfirm(null);
    }
  };

  const handlePreviewTemplate = async (templateId: string) => {
    setLoadingPreview(true);
    setPreviewTemplateId(templateId);
    setTemplateData(null);
    try {
      const data = await getTemplate(templateId);
      setTemplateData(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load template details');
      setPreviewTemplateId(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  if (!wabaId) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            Error: WABA ID not found. Please log in again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Page header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Message templates</h1>
            <p className="text-sm text-gray-600">WABA ID: {wabaId}</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/events/whatsapp-template')}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm hover:bg-blue-700"
            >
              Create template
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Update success/error messages */}
        {/* Removed update success/error messages as update functionality is removed */}

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="text-left text-sm text-gray-600">
                  <th className="px-5 py-3">Template name</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Language</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Form URL</th>
                  <th className="px-5 py-3">Last edited</th>
                  <th className="px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      {[...Array(7)].map((__, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                )}

                {!loading && rows.length === 0 && (
                  <tr className="border-t border-gray-100">
                    <td className="px-5 py-10 text-center text-sm text-gray-500" colSpan={7}>
                      No message templates yet.
                    </td>
                  </tr>
                )}

                {!loading && rows.map((t) => {
                  const badge = statusBadge(t.status);
                  const lang = LANGUAGE_LABELS[t.language || ''] || t.language || '--';
                  
                  return (
                    <tr key={t.id} className="border-t border-gray-100">
                      <td className="px-5 py-4">
                        <div className="font-medium text-gray-900">{t.name}</div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">{t.category || '--'}</td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        <div>{lang}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        {t.form_url ? (
                          <a 
                            href={t.form_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View Form
                          </a>
                        ) : (
                          '--'
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">{formatDate(t.last_updated_time)}</td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => handlePreviewTemplate(t.id)}
                          className="rounded-md bg-purple-100 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-200"
                        >
                          Preview
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer: paging */}
          <div className="flex items-center justify-between px-5 py-3 text-sm text-gray-600">
            <div>
              {rows.length} message templates shown
            </div>
            <div className="space-x-2">
              <button
                className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-50"
                onClick={() => setBefore(cursor.before)}
                disabled={!cursor.before || loading}
              >
                ← Prev
              </button>
              <button
                className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-50"
                onClick={() => setAfter(cursor.after)}
                disabled={!cursor.after || loading}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Template Preview Modal */}
      {previewTemplateId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Template Preview</h3>
              <button
                onClick={() => {
                  setPreviewTemplateId(null);
                  setTemplateData(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {loadingPreview ? (
                <div className="text-center py-8">Loading template...</div>
              ) : templateData ? (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{templateData.name}</h4>
                        <p className="text-sm text-gray-600">Category: {templateData.category} | Language: {templateData.language}</p>
                      </div>
                      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${
                        templateData.status === 'APPROVED' ? 'bg-green-100 text-green-700 border-green-200' :
                        templateData.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                        'bg-red-100 text-red-700 border-red-200'
                      }`}>
                        {templateData.status}
                      </span>
                    </div>
                  </div>

                  {/* Visual Preview */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Message Preview:</h5>
                    <div className="bg-white rounded-lg p-4 space-y-3 shadow-sm">
                      {templateData.components?.map((comp: any, idx: number) => {
                        if (comp.type === 'HEADER') {
                          if (comp.format === 'IMAGE' && comp.example?.header_handle?.[0]) {
                            return (
                              <div key={idx} className="mb-3">
                                <img src={comp.example.header_handle[0]} alt="Header" className="max-w-full rounded" />
                              </div>
                            );
                          } else if (comp.format === 'TEXT' && comp.text) {
                            return (
                              <div key={idx} className="font-semibold text-gray-900 mb-2">{comp.text}</div>
                            );
                          } else if (comp.format === 'VIDEO' && comp.example?.header_handle?.[0]) {
                            return (
                              <div key={idx} className="mb-3">
                                <video src={comp.example.header_handle[0]} controls className="max-w-full rounded" />
                              </div>
                            );
                          } else if (comp.format === 'DOCUMENT' && comp.example?.header_handle?.[0]) {
                            return (
                              <div key={idx} className="text-sm text-gray-600 mb-2">📄 Document attached</div>
                            );
                          }
                        } else if (comp.type === 'BODY' && comp.text) {
                          return (
                            <div key={idx} className="text-sm text-gray-700 whitespace-pre-wrap">
                              {comp.text}
                            </div>
                          );
                        } else if (comp.type === 'FOOTER' && comp.text) {
                          return (
                            <div key={idx} className="text-xs text-gray-500 mt-2">{comp.text}</div>
                          );
                        } else if (comp.type === 'BUTTONS' && comp.buttons) {
                          return (
                            <div key={idx} className="space-y-2 mt-4">
                              {comp.buttons.map((btn: any, btnIdx: number) => (
                                <div key={btnIdx} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm font-medium text-blue-700">
                                  {btn.type === 'URL' && `${btn.text} (${btn.url})`}
                                  {btn.type === 'PHONE_NUMBER' && `${btn.text} (${btn.phone_number})`}
                                  {btn.type === 'QUICK_REPLY' && btn.text}
                                  {btn.type === 'OTP' && 'OTP Button'}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>

                  {/* JSON View (Collapsible) */}
                  <details className="bg-gray-50 p-4 rounded-lg">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                      View Raw JSON
                    </summary>
                    <pre className="mt-3 bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                      {JSON.stringify(templateData, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No template data available</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
